import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Plus,
    Calendar,
    ArrowLeft,
    Loader2,
    Download,
    Palmtree,
    Check,
    X,
    Search,
    Save,
    Users,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axiosInstance from '../utils/axiosConfig';
import toast from 'react-hot-toast';
import {
    EstadoBadge,
    EmptyState,
    TabButton,
    RechazoModal,
    CalendarioGH,
    formatFecha,
    formatFechaDia,
    FiltrosGH,
    filtrarGH,
    descargarBlob,
    AvisoSolapamiento,
    AvisosReglas,
} from './gh';

// Solo GH administra este módulo; admin, TI y captación lo ven como cualquier trabajador
const ROLES_GH = ['gh'];

const ESTADOS_VACACIONES = [
    { value: 'solicitada', label: 'Solicitada' },
    { value: 'aprobada', label: 'Aprobada' },
    { value: 'registrada', label: 'Registrada' },
    { value: 'rechazada', label: 'Rechazada' },
    { value: 'cancelada', label: 'Cancelada' },
];

const DIAS_CORTOS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

const btnPrimario = 'inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-sura-blue text-white text-sm font-medium hover:bg-sura-blue-dark transition-colors disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sura-blue';
const btnSecundario = 'inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sura-blue';
const inputBase = 'w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-sura-blue focus:border-sura-blue outline-none';
const tarjeta = 'bg-white rounded-2xl border border-gray-200';

const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;

// ============================================================
// Calendario de la salida: cada día de las vacaciones en su semana,
// hábil, descanso o feriado, hasta el reintegro
// ============================================================

const CalendarioSalida = ({ calculo }) => {
    const semanas = useMemo(() => {
        if (!calculo?.dias?.length) return [];
        const porFecha = Object.fromEntries(calculo.dias.map((d) => [d.fecha, d]));
        const inicio = new Date(`${calculo.fecha_inicio}T00:00:00`);
        const fin = new Date(`${calculo.fecha_reintegro}T00:00:00`);
        const cursor = new Date(inicio);
        cursor.setDate(cursor.getDate() - ((cursor.getDay() + 6) % 7));   // lunes de esa semana
        const filas = [];
        while (cursor <= fin) {
            const fila = [];
            for (let i = 0; i < 7; i += 1) {
                const iso = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(cursor.getDate()).padStart(2, '0')}`;
                fila.push({
                    iso,
                    dia: cursor.getDate(),
                    mes: cursor.getMonth(),
                    info: porFecha[iso],
                    reintegro: iso === calculo.fecha_reintegro,
                });
                cursor.setDate(cursor.getDate() + 1);
            }
            filas.push(fila);
        }
        return filas;
    }, [calculo]);

    if (!semanas.length) return null;
    const mesesNombre = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

    return (
        <div>
            <div className="grid grid-cols-7 gap-1 max-w-md">
                {DIAS_CORTOS.map((d, i) => (
                    <div key={i} className="text-[11px] text-center text-gray-400 pb-1">{d}</div>
                ))}
                {semanas.flat().map((c) => {
                    const tipo = c.info?.tipo;
                    let estilo = 'text-gray-300';
                    if (tipo === 'habil') estilo = 'bg-sura-blue text-white';
                    else if (tipo === 'descanso') estilo = 'bg-gray-100 text-gray-500';
                    else if (tipo === 'feriado') estilo = 'bg-cyan-100 text-cyan-900 ring-1 ring-inset ring-cyan-300';
                    else if (c.reintegro) estilo = 'bg-white text-sura-blue ring-2 ring-inset ring-cyan-400 font-semibold';
                    const primero = c.dia === 1 || c.iso === calculo.fecha_inicio;
                    return (
                        <div
                            key={c.iso}
                            title={c.info?.descripcion || (c.reintegro ? 'Reintegro' : undefined)}
                            className={`h-9 rounded-lg flex flex-col items-center justify-center text-xs tabular-nums ${estilo}`}
                        >
                            <span>{c.dia}</span>
                            {primero && (tipo || c.reintegro) && <span className="text-[9px] leading-none opacity-75">{mesesNombre[c.mes]}</span>}
                        </div>
                    );
                })}
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-xs text-gray-600">
                <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-sura-blue" />{plural(calculo.dias_habiles, 'hábil', 'hábiles')}</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-gray-200" />{plural(calculo.dias_descanso, 'de descanso', 'de descanso')}</span>
                {calculo.dias_feriados > 0 && (
                    <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-cyan-100 ring-1 ring-cyan-300" />{plural(calculo.dias_feriados, 'feriado', 'feriados')}</span>
                )}
                <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded ring-2 ring-cyan-400" />reintegro</span>
            </div>
            {calculo.feriados?.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                    Feriados: {calculo.feriados.map((f) => `${f.descripcion} (${formatFecha(f.fecha)})`).join(', ')}
                </p>
            )}
        </div>
    );
};

const ResumenSalida = ({ calculo }) => {
    if (!calculo) return null;
    return (
        <p className="text-sm text-gray-700 leading-relaxed">
            Sale el <strong>{formatFechaDia(calculo.fecha_inicio)}</strong>, su último día es el{' '}
            <strong>{formatFechaDia(calculo.fecha_fin)}</strong> y se reintegra el{' '}
            <strong>{formatFechaDia(calculo.fecha_reintegro)}</strong>: {plural(calculo.dias_continuos, 'día continuo', 'días continuos')} fuera.
        </p>
    );
};

// ============================================================
// Saldo por período
// ============================================================

const PeriodoBarra = ({ p, proximo }) => {
    const usados = p.usados || 0;
    const pct = p.dias ? Math.round((usados / p.dias) * 100) : 0;
    return (
        <div className="space-y-1.5">
            <div className="flex items-baseline justify-between gap-3">
                <span className="text-sm font-medium text-gray-900">Período {p.etiqueta}</span>
                <span className="text-sm tabular-nums text-gray-700">
                    <strong className="text-gray-900">{p.disponibles}</strong> de {p.dias} días hábiles
                </span>
            </div>
            <div className="h-2 rounded-full bg-cyan-100 overflow-hidden" aria-hidden="true">
                <div className="h-full bg-sura-blue" style={{ width: `${pct}%` }} />
            </div>
            <p className={`text-xs ${p.vencido ? 'text-amber-700' : 'text-gray-500'}`}>
                {proximo
                    ? `Nace el ${formatFecha(p.nace)}.`
                    : p.vencido
                        ? `Debían tomarse antes del ${formatFecha(p.vence)}.`
                        : `Se pueden tomar hasta el ${formatFecha(p.vence)}.`}
                {usados > 0 && ` Pedidos: ${usados}.`}
            </p>
        </div>
    );
};

const PanelSaldo = ({ saldo, onPedir, titulo = 'Tus vacaciones' }) => {
    if (!saldo) return null;
    const { periodos = [], proximo, anios, ingreso } = saldo;
    return (
        <section className={`${tarjeta} p-5 sm:p-6`}>
            <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
                <div>
                    <h2 className="text-lg font-semibold text-gray-900">{titulo}</h2>
                    <p className="text-sm text-gray-500">
                        Ingresó el {formatFecha(ingreso)}: {plural(anios, 'año', 'años')} en SURA.
                        {saldo.saldo > 0 ? ` Hoy tiene ${plural(saldo.saldo, 'día hábil disponible', 'días hábiles disponibles')}.` : ''}
                    </p>
                </div>
                {onPedir && (
                    <button onClick={onPedir} className={btnPrimario}>
                        <Plus size={16} /> Pedir vacaciones
                    </button>
                )}
            </div>
            <div className="space-y-5">
                {periodos.map((p) => <PeriodoBarra key={p.etiqueta} p={p} />)}
                {proximo && <PeriodoBarra p={proximo} proximo />}
            </div>
        </section>
    );
};

// ============================================================
// Fila de una solicitud en las listas
// ============================================================

const FilaVacaciones = ({ s, onClick, conNombre, accion }) => (
    <button
        type="button"
        onClick={onClick}
        className={`${tarjeta} w-full text-left p-4 hover:border-gray-300 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sura-blue`}
    >
        <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
                {conNombre && <p className="text-sm font-semibold text-gray-900 truncate">{s.empleado_nombre}</p>}
                <p className="text-sm text-gray-700 tabular-nums">
                    {formatFecha(s.fecha_inicio)} al {formatFecha(s.fecha_fin)}
                    <span className="text-gray-400"> | </span>
                    {plural(s.dias_habiles, 'día hábil', 'días hábiles')}
                    <span className="text-gray-400"> | </span>
                    reintegro {formatFecha(s.fecha_reintegro)}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                    {s.codigo}, período {s.periodo}
                    {conNombre && s.empleado_departamento ? `, ${s.empleado_departamento}` : ''}
                </p>
            </div>
            <div className="flex items-center gap-3">
                {accion && <span className="text-xs font-medium text-sura-blue">{accion}</span>}
                <EstadoBadge estado={s.estado} />
            </div>
        </div>
    </button>
);

// ============================================================
// Elegir salida y días (lo usan pedir, aprobar y registrar)
// ============================================================

const SelectorSalida = ({ opciones, fecha, setFecha, dias, setDias, saldoDisponible, libre }) => {
    const enLista = opciones.some((o) => o.fecha === fecha);
    return (
        <div className="space-y-5">
            <div>
                <p className="text-sm font-medium text-gray-900 mb-1">¿Cuándo sale?</p>
                <p className="text-xs text-gray-500 mb-3">Las vacaciones empiezan el 1 o el 16 de cada mes; si ese día no le toca trabajar, el siguiente día hábil.</p>
                <div className="flex flex-wrap gap-2">
                    {opciones.map((o) => (
                        <button
                            key={o.fecha}
                            type="button"
                            onClick={() => setFecha(o.fecha)}
                            aria-pressed={fecha === o.fecha}
                            className={`px-3 py-2 rounded-xl border text-sm tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sura-blue ${
                                fecha === o.fecha ? 'bg-sura-blue text-white border-sura-blue' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
                            }`}
                        >
                            {formatFechaDia(o.fecha)}
                        </button>
                    ))}
                </div>
                {libre && (
                    <label className="flex items-center gap-2 mt-3 text-sm text-gray-600">
                        Otra fecha:
                        <input type="date" value={enLista ? '' : fecha} onChange={(e) => setFecha(e.target.value)} className={`${inputBase} w-auto`} />
                    </label>
                )}
            </div>
            <div>
                <label htmlFor="vac-dias" className="text-sm font-medium text-gray-900">¿Cuántos días hábiles?</label>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                    <input
                        id="vac-dias" type="number" min="1" max="60" value={dias}
                        onChange={(e) => setDias(e.target.value ? Number(e.target.value) : '')}
                        className={`${inputBase} w-24 tabular-nums`}
                    />
                    {saldoDisponible > 0 && dias !== saldoDisponible && (
                        <button type="button" onClick={() => setDias(saldoDisponible)} className={btnSecundario}>
                            Todos ({saldoDisponible})
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

// Calcula en el servidor (con debounce) mientras se eligen fecha y días
const useCalculo = ({ fecha, dias, empleadoId, excluirId, sustituto, activo = true }) => {
    const [res, setRes] = useState({ calculo: null, errores: [], avisos: [] });
    const [cargando, setCargando] = useState(false);
    useEffect(() => {
        if (!activo || !fecha || !dias) {
            setRes({ calculo: null, errores: [], avisos: [] });
            return undefined;
        }
        let vivo = true;
        setCargando(true);
        const t = setTimeout(() => {
            axiosInstance.post('/api/vacaciones/calcular', {
                fecha_inicio: fecha, dias_habiles: dias, empleado_id: empleadoId,
                excluir_id: excluirId, requiere_sustituto: sustituto,
            })
                .then((r) => { if (vivo) setRes(r.data); })
                .catch(() => { if (vivo) setRes({ calculo: null, errores: ['No se pudo calcular; intenta de nuevo'], avisos: [] }); })
                .finally(() => { if (vivo) setCargando(false); });
        }, 300);
        return () => { vivo = false; clearTimeout(t); };
    }, [fecha, dias, empleadoId, excluirId, sustituto, activo]);
    return { ...res, cargando };
};

// ============================================================
// Formulario: pedir (trabajador) o cargar (GH por otra persona)
// ============================================================

const FormularioVacaciones = ({ empleadoInicial, esCargaGH, saldosGH, onVolver, onCreada }) => {
    const [empleadoId, setEmpleadoId] = useState(empleadoInicial || null);
    const [buscar, setBuscar] = useState('');
    const [saldo, setSaldo] = useState(null);
    const [fecha, setFecha] = useState('');
    const [dias, setDias] = useState('');
    const [supervisorId, setSupervisorId] = useState('');
    const [observaciones, setObservaciones] = useState('');
    const [sustituto, setSustituto] = useState(false);
    const [candidato, setCandidato] = useState('');
    const [enviando, setEnviando] = useState(false);

    useEffect(() => {
        if (esCargaGH && !empleadoId) return;
        const params = esCargaGH ? { empleado_id: empleadoId } : {};
        axiosInstance.get('/api/vacaciones/saldo', { params })
            .then((r) => {
                setSaldo(r.data);
                setFecha(r.data.opciones_salida?.[0]?.fecha || '');
                const disponibles = r.data.saldo || r.data.proximo?.disponibles || '';
                setDias(disponibles || '');
                setSupervisorId(r.data.aprobacion?.opciones?.[0]?.id || '');
            })
            .catch(() => toast.error('No se pudo cargar el saldo de vacaciones'));
    }, [empleadoId, esCargaGH]);

    const { calculo, errores, avisos, cargando } = useCalculo({
        fecha, dias, empleadoId: esCargaGH ? empleadoId : undefined, sustituto,
    });

    const candidatos = useMemo(() => {
        if (!esCargaGH || !saldosGH) return [];
        const q = buscar.trim().toLowerCase();
        if (!q) return [];
        return saldosGH.filter((e) => `${e.nombre} ${e.ficha}`.toLowerCase().includes(q)).slice(0, 8);
    }, [buscar, saldosGH, esCargaGH]);

    const enviar = async () => {
        setEnviando(true);
        try {
            const payload = { fecha_inicio: fecha, dias_habiles: dias, observaciones };
            if (esCargaGH) {
                Object.assign(payload, { empleado_id: empleadoId, requiere_sustituto: sustituto, candidato_sustituto: candidato, observaciones_gh: observaciones });
            } else if (supervisorId) {
                payload.supervisor_id = Number(supervisorId);
            }
            const r = await axiosInstance.post('/api/vacaciones/', payload);
            toast.success(r.data.estado === 'registrada' ? 'Vacaciones registradas' : (esCargaGH ? 'Vacaciones enviadas para aprobar' : 'Vacaciones pedidas'));
            onCreada(r.data);
        } catch (err) {
            toast.error(err.response?.data?.error || 'No se pudieron guardar las vacaciones');
        } finally {
            setEnviando(false);
        }
    };

    const sinDias = saldo && !saldo.saldo && !saldo.proximo;
    const yaPasaron = !!calculo && calculo.fecha_inicio <= new Date().toLocaleDateString('en-CA');
    const aprobacion = saldo?.aprobacion;
    const saldoParaFecha = calculo?.saldo ?? saldo?.saldo;

    return (
        <div className="max-w-3xl mx-auto space-y-6">
            <div className="flex items-center gap-3">
                <button onClick={onVolver} className="p-2 rounded-xl hover:bg-gray-100 transition-colors" aria-label="Volver">
                    <ArrowLeft size={20} />
                </button>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">{esCargaGH ? 'Cargar vacaciones' : 'Pedir vacaciones'}</h1>
                    <p className="text-sm text-gray-500">
                        {esCargaGH
                            ? 'Si ya las disfrutó, quedan registradas de una vez. Si son por venir, le llegan a su jefe para aprobar.'
                            : 'Tu jefe las aprueba y Gestión Humana las registra.'}
                    </p>
                </div>
            </div>

            {esCargaGH && !empleadoId && (
                <section className={`${tarjeta} p-5`}>
                    <label htmlFor="vac-buscar" className="text-sm font-medium text-gray-900">¿De quién son?</label>
                    <div className="relative mt-2">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input id="vac-buscar" value={buscar} onChange={(e) => setBuscar(e.target.value)} placeholder="Nombre o ficha" className={`${inputBase} pl-9`} autoFocus />
                    </div>
                    <div className="mt-2 divide-y divide-gray-100">
                        {candidatos.map((e) => (
                            <button key={e.empleado_id} type="button" onClick={() => setEmpleadoId(e.empleado_id)} className="w-full text-left py-2.5 px-1 hover:bg-gray-50 rounded-lg">
                                <span className="text-sm font-medium text-gray-900">{e.nombre}</span>
                                <span className="text-xs text-gray-500 ml-2">{e.ficha}, {e.cargo || 'sin cargo'}, {plural(e.saldo, 'día disponible', 'días disponibles')}</span>
                            </button>
                        ))}
                    </div>
                </section>
            )}

            {saldo && (
                <PanelSaldo
                    saldo={saldo}
                    titulo={esCargaGH ? saldo.empleado?.nombre : 'Tu saldo'}
                />
            )}

            {saldo && sinDias && (
                <EmptyState icon={Palmtree} message="No tiene días de vacaciones todavía" />
            )}

            {saldo && !sinDias && (
                <section className={`${tarjeta} p-5 sm:p-6 space-y-6`}>
                    <SelectorSalida
                        opciones={saldo.opciones_salida || []}
                        fecha={fecha} setFecha={setFecha}
                        dias={dias} setDias={setDias}
                        saldoDisponible={saldoParaFecha}
                        libre={esCargaGH}
                    />

                    <div className="min-h-[2rem]">
                        {cargando && !calculo && <Loader2 size={18} className="animate-spin text-gray-400" />}
                        {calculo && (
                            <div className="space-y-4">
                                <ResumenSalida calculo={calculo} />
                                <CalendarioSalida calculo={calculo} />
                                {calculo.periodos && <p className="text-xs text-gray-500">Salen del período {calculo.periodos}.</p>}
                            </div>
                        )}
                    </div>

                    <AvisosReglas errores={errores} avisos={avisos} />

                    {calculo && saldo.empleado?.id && (
                        <AvisoSolapamiento empleadoId={saldo.empleado.id} fechaInicio={calculo.fecha_inicio} fechaFin={calculo.fecha_fin} />
                    )}

                    {esCargaGH && (
                        <div className="space-y-3">
                            <label className="flex items-center gap-2 text-sm text-gray-700">
                                <input type="checkbox" checked={sustituto} onChange={(e) => setSustituto(e.target.checked)} className="rounded border-gray-300 text-sura-blue focus:ring-sura-blue" />
                                Requiere sustituto
                            </label>
                            {sustituto && (
                                <input value={candidato} onChange={(e) => setCandidato(e.target.value)} placeholder="Candidato (opcional)" className={inputBase} />
                            )}
                        </div>
                    )}

                    {!esCargaGH && aprobacion && (
                        <div className="text-sm text-gray-700">
                            {aprobacion.gh ? (
                                <p>Las aprueba Gestión Humana. {aprobacion.motivo}.</p>
                            ) : aprobacion.opciones.length > 1 ? (
                                <label className="block">
                                    <span className="font-medium text-gray-900">¿Quién las aprueba?</span>
                                    <select value={supervisorId} onChange={(e) => setSupervisorId(e.target.value)} className={`${inputBase} mt-2`}>
                                        {aprobacion.opciones.map((o) => <option key={o.id} value={o.id}>{o.nombre}{o.cargo ? `, ${o.cargo}` : ''}</option>)}
                                    </select>
                                </label>
                            ) : (
                                <p>Las aprueba {aprobacion.opciones[0]?.nombre}.</p>
                            )}
                        </div>
                    )}

                    <div>
                        <label htmlFor="vac-obs" className="text-sm font-medium text-gray-900">Observaciones <span className="font-normal text-gray-400">(opcional)</span></label>
                        <textarea id="vac-obs" rows={2} value={observaciones} onChange={(e) => setObservaciones(e.target.value)} className={`${inputBase} mt-2 resize-none`} />
                    </div>

                    <div className="flex justify-end">
                        <button onClick={enviar} disabled={enviando || cargando || !calculo || errores.length > 0} className={btnPrimario}>
                            {enviando ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                            {!esCargaGH ? 'Pedir vacaciones' : (yaPasaron ? 'Registrar vacaciones' : 'Enviar para aprobar')}
                        </button>
                    </div>
                </section>
            )}
        </div>
    );
};

// ============================================================
// Detalle: ver, aprobar (jefe), registrar (GH), cancelar
// ============================================================

const Pasos = ({ s }) => {
    const pasos = [
        { titulo: 'Solicitada', detalle: `${formatFecha(s.created_at)}${s.cargada_por_gh ? ', cargada por Gestión Humana' : ''}`, hecho: true },
        s.cargada_por_gh && s.estado === 'registrada' && !s.fecha_aprobacion ? {
            titulo: 'Aprobada fuera del sistema',
            detalle: 'Gestión Humana las cargó como ya disfrutadas',
            hecho: true,
        } : {
            titulo: s.aprueba_gh ? 'Aprobada por Gestión Humana' : `Aprobada por ${s.supervisor_nombre || 'su jefe'}`,
            detalle: s.fecha_aprobacion ? `${s.aprobado_por_nombre ? `${s.aprobado_por_nombre}, ` : ''}${formatFecha(s.fecha_aprobacion)}${s.fechas_movidas ? ', con fechas movidas' : ''}` : (s.aprueba_gh ? s.motivo_gh : 'Pendiente'),
            hecho: !!s.fecha_aprobacion,
        },
        { titulo: 'Registrada', detalle: s.fecha_registro ? `${s.gh_responsable_nombre || 'Gestión Humana'}, ${formatFecha(s.fecha_registro)}` : 'Pendiente', hecho: !!s.fecha_registro },
    ];
    const cerrada = ['rechazada', 'cancelada'].includes(s.estado);
    return (
        <ol className="space-y-3">
            {pasos.map((p, i) => (
                <li key={p.titulo} className="flex gap-3">
                    <span className={`mt-0.5 w-6 h-6 shrink-0 rounded-full flex items-center justify-center text-xs font-semibold ${
                        p.hecho ? 'bg-sura-blue text-white' : 'bg-gray-100 text-gray-400'
                    }`}>
                        {p.hecho ? <Check size={13} /> : i + 1}
                    </span>
                    <div>
                        <p className={`text-sm font-medium ${p.hecho ? 'text-gray-900' : 'text-gray-500'}`}>{p.titulo}</p>
                        <p className="text-xs text-gray-500">{p.detalle}</p>
                    </div>
                </li>
            ))}
            {cerrada && (
                <li className="text-sm text-red-700 pl-9">
                    {s.estado === 'rechazada' ? `Rechazada: ${s.motivo_rechazo}` : 'Cancelada'}
                </li>
            )}
        </ol>
    );
};

const Dato = ({ label, children }) => (
    <div>
        <dt className="text-xs text-gray-500">{label}</dt>
        <dd className="text-sm font-medium text-gray-900 tabular-nums">{children || '-'}</dd>
    </div>
);

const DetalleVacaciones = ({ id, user, esGH, onVolver, onCambio }) => {
    const [s, setS] = useState(null);
    const [moverFechas, setMoverFechas] = useState(false);
    const [fecha, setFecha] = useState('');
    const [dias, setDias] = useState('');
    const [opciones, setOpciones] = useState([]);
    const [sustituto, setSustituto] = useState(false);
    const [candidato, setCandidato] = useState('');
    const [comentario, setComentario] = useState('');
    const [enviando, setEnviando] = useState(false);
    const [rechazo, setRechazo] = useState({ abierto: false, motivo: '' });

    const cargar = useCallback(async () => {
        try {
            const r = await axiosInstance.get(`/api/vacaciones/${id}`);
            setS(r.data);
            setFecha(r.data.fecha_inicio);
            setDias(r.data.dias_habiles);
            setSustituto(!!r.data.requiere_sustituto);
            setCandidato(r.data.candidato_sustituto || '');
        } catch {
            toast.error('No se pudo cargar la solicitud');
        }
    }, [id]);

    useEffect(() => { cargar(); }, [cargar]);

    const esJefe = s && s.supervisor_id && s.supervisor_id === user?.empleado_id && s.estado === 'solicitada';
    const puedeRegistrar = s && esGH && (s.estado === 'aprobada' || (s.estado === 'solicitada' && s.aprueba_gh));
    const decide = esJefe || puedeRegistrar;

    useEffect(() => {
        if (!decide || !moverFechas || !s) return;
        axiosInstance.get('/api/vacaciones/saldo', { params: esGH ? { empleado_id: s.empleado_id } : {} })
            .then((r) => setOpciones(r.data.opciones_salida || []))
            .catch(() => setOpciones([]));
    }, [decide, moverFechas, s, esGH]);

    // Calendario siempre; si mueve fechas, recalcula con lo nuevo
    const { calculo, errores, avisos, cargando } = useCalculo({
        fecha: moverFechas ? fecha : s?.fecha_inicio,
        dias: moverFechas ? dias : s?.dias_habiles,
        excluirId: id,
        sustituto,
        activo: !!s,
    });

    const accion = async (ruta, payload, mensaje) => {
        setEnviando(true);
        try {
            await axiosInstance.post(`/api/vacaciones/${id}/${ruta}`, payload);
            toast.success(mensaje);
            await cargar();
            onCambio();
        } catch (err) {
            toast.error(err.response?.data?.error || 'No se pudo completar');
        } finally {
            setEnviando(false);
        }
    };

    const confirmar = () => {
        const payload = { requiere_sustituto: sustituto, candidato_sustituto: candidato };
        if (moverFechas) Object.assign(payload, { fecha_inicio: fecha, dias_habiles: dias });
        if (esJefe) accion('aprobar', { ...payload, observaciones_supervisor: comentario }, 'Vacaciones aprobadas');
        else accion('registrar', { ...payload, observaciones_gh: comentario }, 'Vacaciones registradas');
    };

    if (!s) {
        return <div className="flex justify-center py-24"><Loader2 size={28} className="animate-spin text-gray-400" /></div>;
    }

    const esDueno = s.empleado_id === user?.empleado_id;
    const puedeCancelar = (esDueno && ['solicitada', 'aprobada'].includes(s.estado)) || (esGH && ['solicitada', 'aprobada', 'registrada'].includes(s.estado));
    const erroresVisibles = moverFechas ? errores : [];
    const avisosVisibles = decide ? avisos : [];

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <button onClick={onVolver} className="p-2 rounded-xl hover:bg-gray-100 transition-colors" aria-label="Volver">
                        <ArrowLeft size={20} />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">{esDueno ? 'Tus vacaciones' : s.empleado_nombre}</h1>
                        <p className="text-sm text-gray-500">{s.codigo}{!esDueno && s.empleado_cargo ? `, ${s.empleado_cargo}` : ''}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <EstadoBadge estado={s.estado} />
                    {s.estado === 'registrada' && (
                        <button
                            onClick={() => descargarBlob(`/api/vacaciones/${s.id}/planilla`, `vacaciones_${s.codigo}.pdf`)}
                            className={btnSecundario}
                        >
                            <Download size={16} /> Planilla
                        </button>
                    )}
                </div>
            </div>

            <div className="grid lg:grid-cols-[1fr_280px] gap-6">
                <section className={`${tarjeta} p-5 sm:p-6 space-y-5`}>
                    <ResumenSalida calculo={calculo || {
                        fecha_inicio: s.fecha_inicio, fecha_fin: s.fecha_fin,
                        fecha_reintegro: s.fecha_reintegro, dias_continuos: s.dias_continuos,
                    }} />
                    {cargando && !calculo ? <Loader2 size={18} className="animate-spin text-gray-400" /> : <CalendarioSalida calculo={calculo} />}
                    <dl className="grid grid-cols-2 sm:grid-cols-3 gap-4 pt-2 border-t border-gray-100">
                        <Dato label="Período">{s.periodo}</Dato>
                        <Dato label="Días hábiles">{s.dias_habiles}</Dato>
                        <Dato label="Ingreso">{formatFecha(s.empleado_fecha_ingreso)}</Dato>
                        <Dato label="Sustituto">{s.requiere_sustituto ? (s.candidato_sustituto || 'Sí') : 'No'}</Dato>
                        {s.saldo_sin_esta !== undefined && <Dato label="Saldo sin esta solicitud">{s.saldo_sin_esta}</Dato>}
                    </dl>
                    {(s.observaciones || s.observaciones_supervisor || s.observaciones_gh) && (
                        <div className="space-y-2 pt-2 border-t border-gray-100 text-sm">
                            {s.observaciones && <p><span className="text-gray-500">Trabajador:</span> {s.observaciones}</p>}
                            {s.observaciones_supervisor && <p><span className="text-gray-500">Jefe:</span> {s.observaciones_supervisor}</p>}
                            {s.observaciones_gh && <p><span className="text-gray-500">Gestión Humana:</span> {s.observaciones_gh}</p>}
                        </div>
                    )}
                </section>

                <aside className={`${tarjeta} p-5 h-fit`}>
                    <Pasos s={s} />
                </aside>
            </div>

            {decide && (
                <section className={`${tarjeta} p-5 sm:p-6 space-y-5`}>
                    <h2 className="text-lg font-semibold text-gray-900">{esJefe ? 'Aprobar' : 'Registrar'}</h2>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input type="checkbox" checked={moverFechas} onChange={(e) => setMoverFechas(e.target.checked)} className="rounded border-gray-300 text-sura-blue focus:ring-sura-blue" />
                        Cambiar la salida o los días
                    </label>
                    {moverFechas && (
                        <SelectorSalida
                            opciones={opciones} fecha={fecha} setFecha={setFecha}
                            dias={dias} setDias={setDias}
                            saldoDisponible={s.saldo_sin_esta} libre={esGH}
                        />
                    )}
                    {(esJefe || s.estado === 'solicitada') && (
                        <div className="space-y-3">
                            <label className="flex items-center gap-2 text-sm text-gray-700">
                                <input type="checkbox" checked={sustituto} onChange={(e) => setSustituto(e.target.checked)} className="rounded border-gray-300 text-sura-blue focus:ring-sura-blue" />
                                Requiere sustituto
                            </label>
                            {sustituto && <input value={candidato} onChange={(e) => setCandidato(e.target.value)} placeholder="Candidato (opcional)" className={inputBase} />}
                        </div>
                    )}
                    <AvisosReglas errores={erroresVisibles} avisos={avisosVisibles} />
                    <AvisoSolapamiento empleadoId={s.empleado_id} fechaInicio={calculo?.fecha_inicio || s.fecha_inicio} fechaFin={calculo?.fecha_fin || s.fecha_fin} excludeVacacionId={s.id} />
                    <div>
                        <label htmlFor="vac-comentario" className="text-sm font-medium text-gray-900">Comentario <span className="font-normal text-gray-400">(opcional)</span></label>
                        <textarea id="vac-comentario" rows={2} value={comentario} onChange={(e) => setComentario(e.target.value)} className={`${inputBase} mt-2 resize-none`} />
                    </div>
                    <div className="flex flex-wrap justify-end gap-2">
                        <button onClick={() => setRechazo({ abierto: true, motivo: '' })} disabled={enviando} className={btnSecundario}>
                            <X size={16} /> Rechazar
                        </button>
                        <button onClick={confirmar} disabled={enviando || (moverFechas && (cargando || erroresVisibles.length > 0))} className={btnPrimario}>
                            {enviando ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                            {esJefe ? 'Aprobar' : (s.estado === 'solicitada' ? 'Aprobar y registrar' : 'Registrar')}
                        </button>
                    </div>
                </section>
            )}

            {puedeCancelar && !decide && (
                <div className="flex justify-end">
                    <button
                        onClick={() => {
                            const texto = s.estado === 'registrada' ? '¿Anular estas vacaciones registradas?' : '¿Cancelar esta solicitud de vacaciones?';
                            if (window.confirm(texto)) accion('cancelar', {}, s.estado === 'registrada' ? 'Vacaciones anuladas' : 'Solicitud cancelada');
                        }}
                        disabled={enviando}
                        className={btnSecundario}
                    >
                        <X size={16} /> {s.estado === 'registrada' ? 'Anular' : 'Cancelar solicitud'}
                    </button>
                </div>
            )}

            <RechazoModal
                isOpen={rechazo.abierto}
                onClose={() => setRechazo({ abierto: false, motivo: '' })}
                onConfirm={async () => {
                    await accion('rechazar', { motivo_rechazo: rechazo.motivo }, 'Solicitud rechazada');
                    setRechazo({ abierto: false, motivo: '' });
                }}
                solicitud={s}
                motivo={rechazo.motivo}
                setMotivo={(m) => setRechazo((r) => ({ ...r, motivo: m }))}
                submitting={enviando}
                titulo="Rechazar vacaciones"
            />
        </div>
    );
};

// ============================================================
// GH: saldos de todos y reglas
// ============================================================

const TablaSaldos = ({ saldos, onCargar }) => {
    const [q, setQ] = useState('');
    const [depto, setDepto] = useState('');
    const deptos = useMemo(() => [...new Set(saldos.map((s) => s.departamento).filter(Boolean))].sort(), [saldos]);
    const filas = saldos.filter((s) =>
        (!q || `${s.nombre} ${s.ficha} ${s.cargo || ''}`.toLowerCase().includes(q.toLowerCase()))
        && (!depto || s.departamento === depto));
    return (
        <div className="space-y-4">
            <div className="flex flex-wrap gap-3">
                <div className="relative flex-1 min-w-[200px]">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, ficha o cargo" className={`${inputBase} pl-9`} />
                </div>
                <select value={depto} onChange={(e) => setDepto(e.target.value)} className={`${inputBase} w-auto`}>
                    <option value="">Todos los departamentos</option>
                    {deptos.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
            </div>
            <div className={`${tarjeta} overflow-x-auto`}>
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                            <th className="px-4 py-3 font-medium">Trabajador</th>
                            <th className="px-4 py-3 font-medium">Ingreso</th>
                            <th className="px-4 py-3 font-medium text-right">Disponibles</th>
                            <th className="px-4 py-3 font-medium">Próximo período</th>
                            <th className="px-4 py-3" />
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {filas.map((s) => (
                            <tr key={s.empleado_id} className="hover:bg-gray-50">
                                <td className="px-4 py-3">
                                    <p className="font-medium text-gray-900">{s.nombre}</p>
                                    <p className="text-xs text-gray-500">{s.ficha}{s.cargo ? `, ${s.cargo}` : ''}</p>
                                </td>
                                <td className="px-4 py-3 tabular-nums text-gray-700">
                                    {formatFecha(s.ingreso)}
                                    <p className="text-xs text-gray-500">{plural(s.anios, 'año', 'años')}</p>
                                </td>
                                <td className="px-4 py-3 text-right tabular-nums">
                                    <span className={s.saldo > 0 ? 'font-semibold text-gray-900' : 'text-gray-400'}>{s.saldo}</span>
                                    {s.periodo_vigente && <p className="text-xs text-gray-500">{s.periodo_vigente}</p>}
                                </td>
                                <td className="px-4 py-3 tabular-nums text-gray-700">
                                    {s.proximo_nace ? `${formatFecha(s.proximo_nace)}, ${s.proximo_dias} días` : '-'}
                                </td>
                                <td className="px-4 py-3 text-right">
                                    <button onClick={() => onCargar(s.empleado_id)} className="text-sm font-medium text-sura-blue hover:underline whitespace-nowrap">
                                        Cargar vacaciones
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {filas.length === 0 && <EmptyState icon={Users} message="Nadie coincide con la búsqueda" />}
            </div>
            <p className="text-xs text-gray-500">{filas.length} de {saldos.length} trabajadores activos.</p>
        </div>
    );
};

const CAMPOS_REGLAS = [
    { titulo: 'Días de vacaciones', campos: [
        { clave: 'dias_base', label: 'Días hábiles el primer año' },
        { clave: 'dias_por_anio', label: 'Días adicionales por año' },
        { clave: 'dias_maximo', label: 'Máximo de días hábiles' },
    ] },
    { titulo: 'Plazos', campos: [
        { clave: 'anticipacion_dias', label: 'Anticipación para pedir (días)' },
        { clave: 'anticipacion_sustituto_dias', label: 'Anticipación con sustituto externo (días)' },
        { clave: 'posponer_meses', label: 'Meses para tomarlas después del aniversario' },
    ] },
    { titulo: 'Partir las vacaciones', campos: [
        { clave: 'fracciones_maximas', label: 'Partes como máximo' },
        { clave: 'fraccion_minima', label: 'Días hábiles mínimos por parte' },
    ] },
];

const ReglasVacaciones = () => {
    const [reglas, setReglas] = useState(null);
    const [guardando, setGuardando] = useState(false);

    useEffect(() => {
        axiosInstance.get('/api/vacaciones/reglas').then((r) => setReglas(r.data)).catch(() => toast.error('No se pudieron cargar las reglas'));
    }, []);

    if (!reglas) return <div className="flex justify-center py-16"><Loader2 size={24} className="animate-spin text-gray-400" /></div>;

    const set = (clave, valor) => setReglas((r) => ({ ...r, [clave]: valor }));
    const alternarDia = (d) => set('dias_salida', reglas.dias_salida.includes(d)
        ? reglas.dias_salida.filter((x) => x !== d)
        : [...reglas.dias_salida, d].sort((a, b) => a - b));

    const guardar = async () => {
        setGuardando(true);
        try {
            const r = await axiosInstance.put('/api/vacaciones/reglas', reglas);
            setReglas(r.data);
            toast.success('Reglas guardadas');
        } catch (err) {
            toast.error(err.response?.data?.error || 'No se pudieron guardar las reglas');
        } finally {
            setGuardando(false);
        }
    };

    return (
        <div className="space-y-5">
            <p className="text-sm text-gray-600 max-w-2xl">
                Vienen de la LOTTT (art. 190) y de la Normativa para Admón. de Vacaciones (rev. 3, 2022). Un cambio aplica a lo que se pida de aquí en adelante.
            </p>
            <div className="grid md:grid-cols-2 gap-5">
                {CAMPOS_REGLAS.map((g) => (
                    <section key={g.titulo} className={`${tarjeta} p-5 space-y-3`}>
                        <h3 className="text-sm font-semibold text-gray-900">{g.titulo}</h3>
                        {g.campos.map((c) => (
                            <label key={c.clave} className="flex items-center justify-between gap-4 text-sm text-gray-700">
                                {c.label}
                                <input type="number" min="0" value={reglas[c.clave] ?? ''} onChange={(e) => set(c.clave, e.target.value === '' ? '' : Number(e.target.value))} className={`${inputBase} w-20 text-right tabular-nums`} />
                            </label>
                        ))}
                    </section>
                ))}
                <section className={`${tarjeta} p-5 space-y-3`}>
                    <h3 className="text-sm font-semibold text-gray-900">Días de salida</h3>
                    <p className="text-xs text-gray-500">Sin ninguno marcado, se puede salir cualquier día.</p>
                    <div className="grid grid-cols-7 gap-1.5">
                        {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                            <button key={d} type="button" onClick={() => alternarDia(d)} aria-pressed={reglas.dias_salida.includes(d)}
                                className={`h-8 rounded-lg text-xs tabular-nums border ${reglas.dias_salida.includes(d) ? 'bg-sura-blue text-white border-sura-blue' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>
                                {d}
                            </button>
                        ))}
                    </div>
                </section>
                <section className={`${tarjeta} p-5 space-y-3`}>
                    <h3 className="text-sm font-semibold text-gray-900">Arranque del módulo</h3>
                    <p className="text-xs text-gray-500">Los períodos que a esta fecha ya no se podían tomar se dan por disfrutados.</p>
                    <input type="date" value={reglas.arranque} onChange={(e) => set('arranque', e.target.value)} className={`${inputBase} w-auto`} />
                </section>
            </div>
            <div className="flex justify-end">
                <button onClick={guardar} disabled={guardando} className={btnPrimario}>
                    {guardando ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Guardar reglas
                </button>
            </div>
        </div>
    );
};

// ============================================================
// Vista principal
// ============================================================

const VacacionesView = () => {
    const { user } = useAuth();
    const esGH = ROLES_GH.includes(user?.role);

    const [vista, setVista] = useState({ modo: 'lista' });   // lista | pedir | cargar | detalle
    const [tab, setTab] = useState('mis');
    const [cargando, setCargando] = useState(true);
    const [datos, setDatos] = useState({ mis_solicitudes: [], por_aprobar: [], por_registrar: [] });
    const [saldo, setSaldo] = useState(null);
    const [todas, setTodas] = useState([]);
    const [saldos, setSaldos] = useState([]);
    const [departamentos, setDepartamentos] = useState([]);

    const [cal, setCal] = useState({ year: new Date().getFullYear(), month: new Date().getMonth() + 1, depto: '', eventos: [] });
    const [filtros, setFiltros] = useState({ q: '', estado: '', departamento: '', desde: '', hasta: '' });
    const [exportando, setExportando] = useState(false);

    const cargarListas = useCallback(async () => {
        try {
            const [lista, sal] = await Promise.all([
                axiosInstance.get('/api/vacaciones/'),
                user?.empleado_id ? axiosInstance.get('/api/vacaciones/saldo') : Promise.resolve({ data: null }),
            ]);
            setDatos(lista.data);
            setSaldo(sal.data);
        } catch {
            toast.error('No se pudieron cargar las vacaciones');
        } finally {
            setCargando(false);
        }
    }, [user?.empleado_id]);

    useEffect(() => { cargarListas(); }, [cargarListas]);

    useEffect(() => {
        if (!esGH) return;
        if (tab === 'todas' && !todas.length) {
            axiosInstance.get('/api/vacaciones/todas').then((r) => setTodas(r.data)).catch(() => {});
            axiosInstance.get('/api/departamentos').then((r) => setDepartamentos(r.data)).catch(() => {});
        }
        if ((tab === 'saldos' || vista.modo === 'cargar') && !saldos.length) {
            axiosInstance.get('/api/vacaciones/saldos').then((r) => setSaldos(r.data)).catch(() => toast.error('No se pudieron cargar los saldos'));
        }
        if (tab === 'calendario' && !departamentos.length) {
            axiosInstance.get('/api/departamentos').then((r) => setDepartamentos(r.data)).catch(() => {});
        }
    }, [tab, esGH, todas.length, saldos.length, departamentos.length, vista.modo]);

    useEffect(() => {
        if (!esGH || tab !== 'calendario') return;
        const params = { year: cal.year, month: cal.month };
        if (cal.depto) params.departamento_id = cal.depto;
        axiosInstance.get('/api/vacaciones/calendario', { params })
            .then((r) => setCal((c) => ({ ...c, eventos: r.data.eventos })))
            .catch(() => {});
    }, [esGH, tab, cal.year, cal.month, cal.depto]);

    const refrescar = () => {
        cargarListas();
        if (esGH) {
            setTodas([]);
            setSaldos([]);
        }
    };

    const eventosPorDia = useMemo(() => {
        const mapa = {};
        cal.eventos.forEach((ev) => {
            const ini = new Date(`${ev.fecha_inicio}T00:00:00`);
            const fin = new Date(`${ev.fecha_fin}T00:00:00`);
            for (let d = new Date(ini); d <= fin; d.setDate(d.getDate() + 1)) {
                if (d.getMonth() + 1 === cal.month && d.getFullYear() === cal.year) {
                    const k = d.getDate();
                    (mapa[k] = mapa[k] || []).push(ev);
                }
            }
        });
        return mapa;
    }, [cal]);

    const todasFiltradas = filtrarGH(todas, filtros, {
        nombre: (s) => `${s.empleado_nombre} ${s.codigo}`,
        estado: (s) => s.estado,
        departamento: (s) => s.empleado_departamento,
        fecha: (s) => s.fecha_inicio,
    });

    const exportar = async () => {
        setExportando(true);
        try {
            await descargarBlob(`/api/vacaciones/export?ids=${todasFiltradas.map((s) => s.id).join(',')}`, 'vacaciones.xlsx');
        } finally {
            setExportando(false);
        }
    };

    if (cargando) {
        return <div className="flex items-center justify-center h-64"><Loader2 size={32} className="animate-spin text-gray-400" /></div>;
    }

    if (vista.modo === 'pedir' || vista.modo === 'cargar') {
        return (
            <div className="max-w-6xl mx-auto p-4 sm:p-6">
                <FormularioVacaciones
                    esCargaGH={vista.modo === 'cargar'}
                    empleadoInicial={vista.empleadoId}
                    saldosGH={saldos}
                    onVolver={() => setVista({ modo: 'lista' })}
                    onCreada={(s) => { refrescar(); setVista({ modo: 'detalle', id: s.id }); }}
                />
            </div>
        );
    }

    if (vista.modo === 'detalle') {
        return (
            <div className="max-w-6xl mx-auto p-4 sm:p-6">
                <DetalleVacaciones
                    id={vista.id} user={user} esGH={esGH}
                    onVolver={() => setVista({ modo: 'lista' })}
                    onCambio={refrescar}
                />
            </div>
        );
    }

    const abrir = (s) => setVista({ modo: 'detalle', id: s.id });
    const puedePedir = saldo && (saldo.saldo > 0 || saldo.proximo);

    return (
        <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-6">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Vacaciones</h1>
                    <p className="text-gray-500 mt-1">Cuántos días te tocan, cuándo puedes salir y en qué va cada solicitud.</p>
                </div>
                {esGH && (
                    <button onClick={() => setVista({ modo: 'cargar' })} className={btnSecundario}>
                        <Plus size={16} /> Cargar vacaciones de alguien
                    </button>
                )}
            </div>

            <div className="flex items-center gap-6 border-b border-gray-200 overflow-x-auto">
                <TabButton label="Mis vacaciones" tab="mis" active={tab} onClick={setTab} />
                {datos.por_aprobar.length > 0 && <TabButton label="Por aprobar" tab="aprobar" active={tab} onClick={setTab} badge={datos.por_aprobar.length} />}
                {esGH && <TabButton label="Por registrar" tab="registrar" active={tab} onClick={setTab} badge={datos.por_registrar.length} />}
                {esGH && <TabButton label="Todas" tab="todas" active={tab} onClick={setTab} />}
                {esGH && <TabButton label="Saldos" tab="saldos" active={tab} onClick={setTab} />}
                {esGH && <TabButton label="Calendario" tab="calendario" active={tab} onClick={setTab} icon={Calendar} />}
                {esGH && <TabButton label="Reglas" tab="reglas" active={tab} onClick={setTab} />}
            </div>

            <AnimatePresence mode="wait">
                <motion.div key={tab} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.12 }} className="space-y-4">
                    {tab === 'mis' && (
                        <>
                            {saldo && <PanelSaldo saldo={saldo} onPedir={puedePedir ? () => setVista({ modo: 'pedir' }) : null} />}
                            {datos.mis_solicitudes.length === 0
                                ? <EmptyState icon={Palmtree} message="Todavía no has pedido vacaciones" />
                                : datos.mis_solicitudes.map((s) => <FilaVacaciones key={s.id} s={s} onClick={() => abrir(s)} />)}
                        </>
                    )}
                    {tab === 'aprobar' && datos.por_aprobar.map((s) => (
                        <FilaVacaciones key={s.id} s={s} conNombre onClick={() => abrir(s)} accion="Revisar y aprobar" />
                    ))}
                    {tab === 'registrar' && (datos.por_registrar.length === 0
                        ? <EmptyState icon={Check} message="No hay vacaciones esperando registro" />
                        : datos.por_registrar.map((s) => (
                            <FilaVacaciones key={s.id} s={s} conNombre onClick={() => abrir(s)}
                                accion={s.estado === 'solicitada' ? 'Aprobar y registrar' : `Aprobada por ${s.aprobado_por_nombre || 'su jefe'}`} />
                        )))}
                    {tab === 'todas' && (
                        <>
                            <FiltrosGH
                                q={filtros.q} setQ={(v) => setFiltros((f) => ({ ...f, q: v }))}
                                estado={filtros.estado} setEstado={(v) => setFiltros((f) => ({ ...f, estado: v }))} estados={ESTADOS_VACACIONES}
                                departamento={filtros.departamento} setDepartamento={(v) => setFiltros((f) => ({ ...f, departamento: v }))} departamentos={departamentos}
                                desde={filtros.desde} setDesde={(v) => setFiltros((f) => ({ ...f, desde: v }))}
                                hasta={filtros.hasta} setHasta={(v) => setFiltros((f) => ({ ...f, hasta: v }))}
                                onExport={exportar} exporting={exportando}
                                total={todas.length} mostrados={todasFiltradas.length}
                            />
                            {todasFiltradas.length === 0
                                ? <EmptyState icon={Palmtree} message={todas.length ? 'Ninguna coincide con los filtros' : 'No hay solicitudes de vacaciones'} />
                                : todasFiltradas.map((s) => <FilaVacaciones key={s.id} s={s} conNombre onClick={() => abrir(s)} />)}
                        </>
                    )}
                    {tab === 'saldos' && (saldos.length
                        ? <TablaSaldos saldos={saldos} onCargar={(id) => setVista({ modo: 'cargar', empleadoId: id })} />
                        : <div className="flex justify-center py-16"><Loader2 size={24} className="animate-spin text-gray-400" /></div>)}
                    {tab === 'calendario' && (
                        <CalendarioGH
                            year={cal.year} month={cal.month}
                            eventosPorDia={eventosPorDia}
                            departamentos={departamentos} departamentoId={cal.depto}
                            onDepartamentoChange={(v) => setCal((c) => ({ ...c, depto: v }))}
                            onPrev={() => setCal((c) => (c.month === 1 ? { ...c, month: 12, year: c.year - 1 } : { ...c, month: c.month - 1 }))}
                            onNext={() => setCal((c) => (c.month === 12 ? { ...c, month: 1, year: c.year + 1 } : { ...c, month: c.month + 1 }))}
                            onToday={() => setCal((c) => ({ ...c, year: new Date().getFullYear(), month: new Date().getMonth() + 1 }))}
                            maxPorDia={2}
                            renderEvento={(ev, i) => (
                                <div key={i} title={`${ev.empleado_nombre}: ${ev.dias_habiles} días hábiles, reintegro ${formatFecha(ev.fecha_reintegro)}`}
                                    className={`text-[10px] leading-tight px-1.5 py-0.5 rounded truncate border ${ev.estado === 'registrada' ? 'bg-sura-blue text-white border-sura-blue' : 'bg-cyan-50 text-cyan-900 border-cyan-200'}`}>
                                    {ev.empleado_nombre?.split(' ')[0]}
                                </div>
                            )}
                            leyenda={(
                                <>
                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border bg-sura-blue text-white border-sura-blue">Registradas</span>
                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border bg-cyan-50 text-cyan-900 border-cyan-200">Aprobadas, sin registrar</span>
                                </>
                            )}
                            resumen={cal.eventos.length ? `${plural(cal.eventos.length, 'persona sale', 'personas salen')} de vacaciones este mes` : null}
                        />
                    )}
                    {tab === 'reglas' && <ReglasVacaciones />}
                </motion.div>
            </AnimatePresence>
        </div>
    );
};

export default VacacionesView;
